import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { supabase } from "@/integrations/supabase/client";

interface GalleryImage {
  url: string;
  caption?: string;
}

interface GalleryProps {
  id: string;
  title: string;
  subtitle: string;
  images: GalleryImage[];
}

// Helper to generate caption from URL if not provided
const getCaption = (image: GalleryImage): string => {
  if (image.caption) return image.caption;
  
  const urlParts = image.url.split("/");
  const filename = urlParts[urlParts.length - 1].split("?")[0];
  return filename
    .replace(/[-_]/g, " ")
    .replace(/\.[^.]+$/, "")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const Gallery = ({ id, title, subtitle, images }: GalleryProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Engagement tracking
  const mountTime = useRef(Date.now());
  const imagesViewed = useRef(0);
  const sessionLogged = useRef(false);

  const logSession = useCallback(() => {
    if (sessionLogged.current) return;
    sessionLogged.current = true;
    const duration = Math.round((Date.now() - mountTime.current) / 1000);
    // Use fetch with keepalive for reliable capture on page close
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/gallery_sessions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify({
        gallery: id,
        duration_seconds: duration,
        images_viewed: imagesViewed.current,
      }),
      keepalive: true,
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    mountTime.current = Date.now();
    imagesViewed.current = 0;
    sessionLogged.current = false;

    const handleBeforeUnload = () => logSession();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      logSession();
    };
  }, [logSession]);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    imagesViewed.current += 1;
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, closeLightbox, goToPrevious, goToNext]);

  const bgClass = id === "residential" ? "bg-background" : "bg-secondary";

  return (
    <section id={id} className={`py-20 ${bgClass}`}>
      <div className="section-container">
        <div className="text-center mb-12">
          <h2 className="section-heading font-heading">{title}</h2>
          <p className="section-subheading mx-auto">{subtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => openLightbox(index)}
              className="gallery-thumb focus:outline-none"
              aria-label={`View ${getCaption(image)}`}
            >
              <LazyLoadImage
                src={image.url}
                alt={getCaption(image)}
                effect="blur"
                className="w-full h-full object-cover"
                wrapperClassName="w-full h-full"
                threshold={100}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="lightbox-overlay p-4 md:p-8"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <div className="lightbox-content flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeLightbox}
              className="absolute top-2 right-2 md:-top-12 md:right-0 z-10 text-card hover:text-card/80 transition-colors focus:outline-none focus:ring-2 focus:ring-card bg-foreground/40 md:bg-transparent rounded-full p-1"
              aria-label="Close lightbox"
            >
              <X className="w-8 h-8" />
            </button>

            <button
              onClick={goToPrevious}
              className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 md:-translate-x-12 text-card hover:text-card/80 transition-colors focus:outline-none focus:ring-2 focus:ring-card bg-foreground/40 md:bg-transparent rounded-full p-2"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 md:translate-x-12 text-card hover:text-card/80 transition-colors focus:outline-none focus:ring-2 focus:ring-card bg-foreground/40 md:bg-transparent rounded-full p-2"
              aria-label="Next image"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            <LazyLoadImage
              src={images[currentIndex].url}
              alt={getCaption(images[currentIndex])}
              effect="blur"
              className="w-full max-h-[80vh] object-contain rounded-lg"
              wrapperClassName="flex justify-center"
            />

            <p className="text-card text-center mt-4 text-lg">
              {getCaption(images[currentIndex])}
              <span className="text-card/60 ml-2">
                ({currentIndex + 1} of {images.length})
              </span>
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
